/**
 * ComplianceChecker — Google Ads compliance panel.
 * Manual text tester + site-wide compliance overview.
 */
import React, { useState, useEffect, useCallback } from "react";

const SEVERITY_ICON  = { critical: "🔴", warning: "🟡", info: "🔵" };
const SEVERITY_CLASS = { critical: "seo-issue-critical", warning: "seo-issue-warning", info: "seo-issue-info" };

function ComplianceScore({ score }) {
  const color = score >= 90 ? "#00ff88" : score >= 70 ? "#f5c518" : "#ff4d6d";
  return (
    <div className="seo-compliance-score-wrap">
      <svg width={80} height={80} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={40} cy={40} r={32} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={8} />
        <circle
          cx={40} cy={40} r={32}
          fill="none"
          stroke={color}
          strokeWidth={8}
          strokeDasharray={`${2 * Math.PI * 32}`}
          strokeDashoffset={`${2 * Math.PI * 32 * (1 - score / 100)}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="seo-compliance-score-center">
        <span style={{ color, fontSize: 18, fontWeight: 700 }}>{score}</span>
      </div>
    </div>
  );
}

export default function ComplianceChecker() {
  const [text, setText]           = useState("");
  const [context, setContext]     = useState("general");
  const [checking, setChecking]   = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [siteAudit, setSiteAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("manual"); // manual | site

  const checkText = async () => {
    if (!text.trim()) return;
    setChecking(true);
    setCheckResult(null);
    try {
      const r = await fetch("/api/seo/audit/compliance/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, context }),
      });
      const d = await r.json();
      if (d.success) setCheckResult(d.result);
    } catch (e) {
      setCheckResult({ error: "Check failed" });
    } finally {
      setChecking(false);
    }
  };

  const loadSiteAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const r = await fetch("/api/seo/audit/compliance");
      const d = await r.json();
      if (d.success) setSiteAudit(d.compliance);
    } catch (e) { console.error(e); }
    finally { setAuditLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === "site" && !siteAudit) loadSiteAudit();
  }, [activeTab, siteAudit, loadSiteAudit]);

  return (
    <div className="seo-compliance-wrap">
      <div className="seo-blog-tabs">
        <button
          className={`seo-filter-tab ${activeTab === "manual" ? "active" : ""}`}
          onClick={() => setActiveTab("manual")}
        >
          ✍️ Manual Check
        </button>
        <button
          className={`seo-filter-tab ${activeTab === "site" ? "active" : ""}`}
          onClick={() => setActiveTab("site")}
        >
          🌐 Site-Wide Audit
        </button>
      </div>

      {/* Manual checker */}
      {activeTab === "manual" && (
        <div className="seo-manual-checker">
          <div className="seo-compliance-info-box">
            <div className="seo-compliance-info-title">Google Ads Safety Checker</div>
            <p className="seo-compliance-info-text">
              Paste any text — product titles, descriptions, ad copy, or blog content — to check
              for Google Ads policy violations before publishing. Checks health claims, superlatives,
              competitor references, and UAE market-specific regulations.
            </p>
          </div>

          <div className="seo-form-row">
            <label className="seo-label">Context</label>
            <select className="seo-input" value={context} onChange={e => setContext(e.target.value)}>
              <option value="general">General</option>
              <option value="meta_title">Meta Title</option>
              <option value="meta_description">Meta Description</option>
              <option value="product_description">Product Description</option>
              <option value="ad_copy">Ad Copy</option>
            </select>
          </div>

          <textarea
            className="seo-input seo-textarea"
            rows={6}
            placeholder="Paste your text here to check for compliance issues…"
            value={text}
            onChange={e => setText(e.target.value)}
          />

          <button
            className="seo-btn seo-btn-primary"
            onClick={checkText}
            disabled={checking || !text.trim()}
            style={{ marginTop: 12 }}
          >
            {checking ? "⟳ Checking…" : "🔍 Check Compliance"}
          </button>

          {checkResult && !checkResult.error && (
            <div className="seo-compliance-result">
              <div className="seo-compliance-result-header">
                <ComplianceScore score={checkResult.compliance_score || 0} />
                <div className="seo-compliance-result-summary">
                  <div className={`seo-compliance-verdict ${checkResult.is_safe ? "safe" : "unsafe"}`}>
                    {checkResult.is_safe ? "✅ SAFE — Google Ads Compliant" : "❌ VIOLATIONS FOUND"}
                  </div>
                  <div className="seo-compliance-counts">
                    {checkResult.violations?.length > 0 && (
                      <span className="seo-issue-critical-badge">
                        {checkResult.violations.length} violation{checkResult.violations.length !== 1 ? "s" : ""}
                      </span>
                    )}
                    {checkResult.warnings?.length > 0 && (
                      <span className="seo-issue-warning-badge">
                        {checkResult.warnings.length} warning{checkResult.warnings.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {checkResult.violations?.length > 0 && (
                <div className="seo-compliance-section">
                  <div className="seo-compliance-section-title">🔴 Violations (must fix)</div>
                  {checkResult.violations.map((v, i) => (
                    <div key={i} className="seo-compliance-issue">
                      <div className="seo-compliance-issue-msg">
                        <strong>{v.category?.replace(/_/g, " ")}:</strong>{" "}
                        {v.message || `Prohibited pattern detected`}
                      </div>
                      {(v.matched || v.matches?.length > 0) && (
                        <div className="seo-compliance-matched">
                          Found: <code>"{v.matched || v.matches?.[0]}"</code>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {checkResult.warnings?.length > 0 && (
                <div className="seo-compliance-section">
                  <div className="seo-compliance-section-title">🟡 Warnings (review)</div>
                  {checkResult.warnings.map((w, i) => (
                    <div key={i} className="seo-compliance-issue seo-compliance-issue-warn">
                      <div className="seo-compliance-issue-msg">{w.message}</div>
                    </div>
                  ))}
                </div>
              )}

              {checkResult.suggestions?.length > 0 && (
                <div className="seo-compliance-section">
                  <div className="seo-compliance-section-title">💡 Suggestions</div>
                  {checkResult.suggestions.map((s, i) => (
                    <div key={i} className="seo-compliance-suggestion">{s}</div>
                  ))}
                </div>
              )}

              {checkResult.is_safe && checkResult.violations?.length === 0 && (
                <div className="seo-compliance-all-ok">
                  No issues found. This text is safe for Google Ads and Shopify.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Site-wide audit */}
      {activeTab === "site" && (
        <div className="seo-site-compliance">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button
              className="seo-btn seo-btn-ghost seo-btn-sm"
              onClick={loadSiteAudit}
              disabled={auditLoading}
            >
              {auditLoading ? "⟳ Scanning…" : "↺ Run Full Audit"}
            </button>
          </div>

          {auditLoading && <div className="seo-loading">Scanning all products for compliance…</div>}

          {siteAudit && !auditLoading && (
            <>
              {/* Summary */}
              <div className="seo-compliance-site-summary">
                <div className="seo-compliance-site-stat">
                  <span className="seo-audit-summary-num seo-text-green">
                    {siteAudit.summary?.compliant_products || 0}
                  </span>
                  <span className="seo-audit-summary-lbl">Compliant</span>
                </div>
                <div className="seo-compliance-site-stat">
                  <span className="seo-audit-summary-num seo-text-red">
                    {siteAudit.summary?.products_with_violations || 0}
                  </span>
                  <span className="seo-audit-summary-lbl">Violations</span>
                </div>
                <div className="seo-compliance-site-stat">
                  <span className="seo-audit-summary-num seo-text-warn">
                    {siteAudit.summary?.total_violations || 0}
                  </span>
                  <span className="seo-audit-summary-lbl">Total Issues</span>
                </div>
                <div className="seo-compliance-site-stat">
                  <span className="seo-audit-summary-num seo-text-blue">
                    {siteAudit.summary?.overall_compliance_rate || 0}%
                  </span>
                  <span className="seo-audit-summary-lbl">Compliance Rate</span>
                </div>
              </div>

              {/* Products with issues */}
              {siteAudit.products?.filter(p => !p.is_safe).length > 0 && (
                <div className="seo-compliance-product-list">
                  <div className="seo-compliance-section-title">Products Needing Attention</div>
                  {siteAudit.products
                    .filter(p => !p.is_safe)
                    .map(p => (
                      <div key={p.product_id} className="seo-compliance-product-row">
                        <div className="seo-compliance-product-title">{p.title}</div>
                        <div>
                          {Object.entries(p.field_results || {}).map(([field, result]) =>
                            !result.is_safe && result.violations?.map((v, i) => (
                              <div key={`${field}-${i}`} className="seo-compliance-issue">
                                <strong>{field}:</strong> {v.message}
                                {v.matched && <code> "{v.matched}"</code>}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {siteAudit.products?.filter(p => !p.is_safe).length === 0 && (
                <div className="seo-compliance-all-ok">
                  ✅ All products pass Google Ads compliance checks.
                </div>
              )}
            </>
          )}

          {!siteAudit && !auditLoading && (
            <div className="seo-empty">Click "Run Full Audit" to scan all products for compliance issues.</div>
          )}
        </div>
      )}
    </div>
  );
}
